// Small helpers for notification text, dates, and read state.

import i18next from 'i18next';
import moment from 'moment';

// Escape text before using it in a regular expression.
export const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// A target is external (e.g. an export download link) when it has a scheme or
// is protocol-relative. Internal app routes start with a single slash.
export const isExternalNotificationTarget = (url?: string | null): boolean => {
  if (!url) return false;
  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
};

// Remove HTML tags from backend text.
export const stripNotificationHtml = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  return String(value)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

// Remove unresolved backend template fields.
export const removeUnresolvedTemplateFields = (
  value?: string | number | null
) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  return String(value)
    .replace(/\{[^}]+\}/g, ' ')
    .replace(/\s+([,.;!?])/g, '$1')
    .replace(/\s+:(?![)(DPpOo/\\])/g, ':')
    .replace(/\s+/g, ' ')
    .replace(/@\s+(?=\S)/g, '@')
    .trim();
};

// Strip HTML and unresolved template fields from notification text.
export const cleanNotificationText = (value?: string | number | null) =>
  removeUnresolvedTemplateFields(stripNotificationHtml(value));

// Convert an ISO date to a localized relative label like "2 hours ago".
// moment.fromNow() is locale-aware via moment.locale() set in use-locale.tsx.
export const formatNotificationRelativeTime = (isoDate: string): string => {
  const m = moment(isoDate);
  return m.isValid() ? m.fromNow() : moment().fromNow();
};

// Build the original-date label shown on updated notifications.
export const formatNotificationOriginalDate = (isoDate: string): string => {
  const m = moment(isoDate);
  if (!m.isValid()) {
    return i18next.t('today', { ns: 'inbox' });
  }

  if (m.isSame(moment(), 'day')) {
    return i18next.t('today', { ns: 'inbox' });
  }

  if (m.isSame(moment().subtract(1, 'day'), 'day')) {
    return i18next.t('yesterday', { ns: 'inbox' });
  }

  return m.format('LL');
};

// Build the date label used for notification groups.
export const getNotificationDateGroupLabel = (createdAt: string) => {
  const created = moment(createdAt);
  if (!created.isValid()) {
    return i18next.t('today', { ns: 'inbox' });
  }

  return created.calendar(null, {
    sameDay: `[${i18next.t('today', { ns: 'inbox' })}]`,
    lastDay: `[${i18next.t('yesterday', { ns: 'inbox' })}]`,
    lastWeek: 'LL',
    sameElse: 'LL',
  });
};

type NotificationDateFields = {
  createdAt: string;
  updatedAt?: string;
  isUpdated?: boolean;
};

// Use updatedAt as the effective date for updated notifications.
export const getNotificationEffectiveDate = <T extends NotificationDateFields>(
  item: T
) => (item.isUpdated && item.updatedAt ? item.updatedAt : item.createdAt);

// Sort notifications newest first using the same effective date as grouping.
export const sortNotificationsByEffectiveDateDesc = <
  T extends NotificationDateFields,
>(
  items: T[]
) =>
  items.slice().sort((a, b) => {
    const aTime = new Date(getNotificationEffectiveDate(a)).getTime();
    const bTime = new Date(getNotificationEffectiveDate(b)).getTime();
    const safeATime = Number.isNaN(aTime) ? 0 : aTime;
    const safeBTime = Number.isNaN(bTime) ? 0 : bTime;

    return safeBTime - safeATime;
  });

// Group notifications by their date label, using updatedAt when the item was updated.
export const groupNotificationsByDate = <T extends NotificationDateFields>(
  items: T[]
): Array<[string, T[]]> => {
  const grouped = items.reduce<Record<string, T[]>>((acc, item) => {
    const dateKey = getNotificationEffectiveDate(item);
    const label = getNotificationDateGroupLabel(dateKey);
    if (!acc[label]) {
      acc[label] = [];
    }
    acc[label].push(item);
    return acc;
  }, {});

  return Object.entries(grouped);
};

// Add or remove an id from a read-id list.
export const getToggledReadIds = (ids: string[], id: string) =>
  ids.includes(id) ? ids.filter((currentId) => currentId !== id) : [...ids, id];

// Build the confirmed_at value for a read-state update.
export const getConfirmedAtValue = (isCurrentlyRead: boolean) =>
  isCurrentlyRead ? null : new Date().toISOString();
